/**
 * Whether an edit may be placed before the server has agreed to it.
 *
 * The client can work out where a row belongs after an edit -- it has the
 * filters, the sorts and the comparators -- right up until one of those rules
 * reads a field only the server can compute. A formula, a rollup, a lookup or a
 * last-modified stamp is not in the edit, so its new value is unknown, and
 * predicting where the row lands would be a guess. A row that jumps to one place
 * and then to another a moment later is worse than one that waits.
 *
 * There was a second function here, `viewCanMoveOrHideRows`, asking whether the
 * view had any rule that *could* move a row. It was a short-circuit and nothing
 * more: in a view with no rules the lifecycle already works out that the row
 * stays where it is, so the two paths were the same path. The mutation pass
 * could not tell them apart, which is the same thing said in a different way.
 */
import type { Field } from '@/api/types'
import type { OrderTerm } from './rowSort'
import type { FilterableView } from './rowMatch'

/** What these two questions read from a view. */
export interface OptimismView extends FilterableView {
  sortings?: readonly OrderTerm[]
  group_bys?: readonly OrderTerm[]
}

/**
 * Whether the client can work out where a row belongs after an edit.
 *
 * `read_only` comes from the API and is the field type's own answer or the
 * field's, whichever is true -- so a formula is read-only wherever it appears
 * and the frontend keeps no table of its own to fall out of step.
 *
 * A search is treated as reading every field, because it does: a term matches
 * against the rendered text of all of them, so one read-only field anywhere in
 * the table is enough to make the match the server's question.
 */
export function canUpdateOptimistically(
  view: OptimismView,
  fields: readonly Field[],
  searchTerm: string,
): boolean {
  const readOnly = new Set(
    fields.filter((field) => field.read_only).map((field) => field.id),
  )
  if (readOnly.size === 0) return true

  // `field` is nullable on a condition -- see `FilterableCondition` -- and a
  // condition naming no column reads none, so `has(null)` answering false is
  // the right answer rather than a case to guard.
  const reads = (terms: readonly { field: number | null }[] | undefined) =>
    (terms ?? []).some((term) => term.field !== null && readOnly.has(term.field))

  const filtering = view.filters_disabled === true ? [] : (view.filters ?? [])

  return !(
    reads(view.sortings) ||
    reads(view.group_bys) ||
    reads(filtering) ||
    searchTerm !== ''
  )
}
